// Client HTTP unique pour parler au backend NSARA (Express).
//
// Règle de sécurité (P0): l'identité de l'utilisateur ne se transmet JAMAIS
// via un email/userId choisi côté client (ancien header `x-user-email`,
// ancien `userId` dans le corps des requêtes cashflow-ai). Elle se transmet
// exclusivement via le Firebase ID Token courant, vérifié côté serveur.
import { getAuthHeadersWithRecovery, forceRefreshSession } from '../config/supabase';

import { API_BASE_CANDIDATES } from './apiBase';
export { API_BASE_CANDIDATES } from './apiBase';

// Authenticated fetch to the single configured API origin.
export const apiFetch = async (path, options = {}) => {
  const authHeaders = await getAuthHeadersWithRecovery();
  let lastError = null;
  for (const base of API_BASE_CANDIDATES) {
    try {
      return await fetch(`${base}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...(options.headers || {})
        }
      });
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error('Backend indisponible.');
};

const AUTH_ERROR_CODES = new Set(['INVALID_TOKEN', 'UNAUTHENTICATED']);

// Mission M1: filet de sécurité côté client. Le backend traduit désormais ses
// messages de validation, mais un message zod brut (anglais, éventuellement
// préfixé d'un nom de champ interne, ex: "businessId: Required") renvoyé par
// un backend plus ancien ne doit jamais être affiché tel quel.
const RAW_VALIDATION_MESSAGE = /^(?:[A-Za-z_][\w.]*: )?(Required|Invalid input|Invalid uuid|Invalid email|Expected .+, received .+|String must contain .+|Number must be .+|Array must contain .+)$/;

export const toUserErrorMessage = (message) => {
  const text = String(message || '').trim();
  const raw = text.match(RAW_VALIDATION_MESSAGE);
  if (!raw) return text;
  return raw[1] === 'Required' ? 'Une information obligatoire est manquante.' : 'Une information saisie est invalide.';
};

// Variante qui parse le JSON et lève une erreur lisible en cas d'échec
// (compatible avec le format d'erreur backend { error: { code, message } }).
// L'erreur levée porte .code et .status pour permettre un affichage UX
// spécifique (ex: stock insuffisant) plutôt qu'un message générique (étape 31).
//
// P0 fix: un token expiré était auparavant renvoyé au backend indéfiniment
// (getAuthHeadersWithRecovery ne vérifiait pas l'expiration, et rien ici ne
// réagissait à un 401) — voir config/supabase.js pour le rafraîchissement
// proactif ajouté là-bas. Ceci couvre le résidu inévitable (un token jugé
// valide côté client mais rejeté côté serveur — dérive d'horloge, session
// mise en cache avant l'ajout de ce correctif, etc.): un seul essai de
// rafraîchissement puis une seule répétition de la requête, jamais plus —
// sûr même pour les écritures, puisqu'un 401 est renvoyé par le middleware
// d'authentification AVANT que la route n'exécute la moindre logique
// métier: la première tentative n'a donc jamais pu créer/modifier quoi que
// ce soit. Si le rafraîchissement échoue aussi, la session est effacée
// (voir clearInvalidSession) et currentUser passe à null, ce qui fait
// automatiquement rediriger ProtectedRoute vers /login (App.js) — sans
// boucle, sans contournement, sans masquer une vraie erreur d'autorisation
// (403 n'est jamais retenté ici, seulement 401 avec un code d'authentification).
export const apiFetchJson = async (path, options = {}, _retried = false) => {
  const response = await apiFetch(path, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = toUserErrorMessage(data?.error?.message || data?.error || `HTTP ${response.status}`);
    const code = data?.error?.code || null;
    if (!_retried && response.status === 401 && AUTH_ERROR_CODES.has(code)) {
      const refreshed = await forceRefreshSession();
      if (refreshed?.access_token) {
        return apiFetchJson(path, options, true);
      }
    }
    const error = new Error(message);
    error.code = code;
    error.status = response.status;
    throw error;
  }
  return data;
};

const qs = (params = {}) => {
  const usable = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (usable.length === 0) return '';
  return `?${new URLSearchParams(usable).toString()}`;
};

// GET /api/me — identité + statut admin dérivés du token, calculés côté serveur.
// À utiliser partout où le frontend doit savoir si l'utilisateur est admin
// plateforme, plutôt que de comparer un email/mot de passe côté client.
export const fetchCurrentUserRole = async () => apiFetchJson('/api/me');

// Upload d'image générique (logo boutique, photo de profil...) via Cloudinary
// côté serveur (voir backend/server.js, POST /media/profile-image — hors
// /api car monté directement sur `app`, pas sur un Router). Un seul point
// d'upload partagé: pas de clé Cloudinary côté client, pas de second
// fournisseur de stockage. Renvoie { url, publicId }.
export const uploadImage = async (file) => {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
    reader.readAsDataURL(file);
  });
  return apiFetchJson('/media/profile-image', { method: 'POST', body: JSON.stringify({ imageData: dataUrl }) });
};

// --- Entreprise / boutiques ---
export const getBusiness = async () => apiFetchJson('/api/business');
export const createBusiness = async (payload) => apiFetchJson('/api/business', { method: 'POST', body: JSON.stringify(payload) });
export const updateBusiness = async (businessId, payload) =>
  apiFetchJson(`/api/business/${businessId}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const getShops = async (businessId, { includeStats } = {}) =>
  apiFetchJson(`/api/shops${qs({ businessId, includeStats: includeStats ? 'true' : '' })}`);
export const createShop = async (payload) => apiFetchJson('/api/shops', { method: 'POST', body: JSON.stringify(payload) });
export const updateShop = async (shopId, payload) =>
  apiFetchJson(`/api/shops/${shopId}`, { method: 'PATCH', body: JSON.stringify(payload) });

// --- Produits ---
export const getProducts = async ({ businessId, shopId, limit, offset } = {}) =>
  apiFetchJson(`/api/products${qs({ businessId, shopId, limit, offset })}`);
export const getProduct = async (productId) => apiFetchJson(`/api/products/${productId}`);
export const createProduct = async (payload) => apiFetchJson('/api/products', { method: 'POST', body: JSON.stringify(payload) });
export const updateProduct = async (productId, payload) =>
  apiFetchJson(`/api/products/${productId}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteProduct = async (productId) => apiFetchJson(`/api/products/${productId}`, { method: 'DELETE' });

// --- Liens produits publics / commandes boutique ---
// Ces appels publics n'ajoutent pas de token Firebase : le backend ne doit pas
// exiger de compte client pour passer une commande depuis un lien produit.
export const getPublicProduct = async (publicId) => {
  const response = await fetch(`${API_BASE_CANDIDATES[0]}/api/public/products/${encodeURIComponent(publicId)}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.error?.message || 'Produit indisponible.');
    error.code = data?.error?.code || null;
    error.status = response.status;
    throw error;
  }
  return data;
};
export const createPublicOrder = async (publicId, payload) => {
  const response = await fetch(`${API_BASE_CANDIDATES[0]}/api/public/products/${encodeURIComponent(publicId)}/orders`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.error?.message || 'Commande impossible.');
    error.code = data?.error?.code || null;
    error.status = response.status;
    throw error;
  }
  return data;
};

// NSARA Stores V2 — vitrine publique d'une boutique (/s/:shopId).
// Même règle que getPublicProduct: aucun token Firebase n'est envoyé, la
// vitrine est lisible par n'importe quel visiteur. Le backend ne renvoie que
// le catalogue public de CETTE boutique (voir backend/routes/publicShops.js).
export const getPublicShop = async (shopId) => {
  const response = await fetch(`${API_BASE_CANDIDATES[0]}/api/public/shops/${encodeURIComponent(shopId)}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.error?.message || 'Boutique indisponible.');
    error.code = data?.error?.code || null;
    error.status = response.status;
    throw error;
  }
  return data;
};

// --- Commandes de boutique (authentifiées) ---
export const getOrders = async ({ businessId, shopId, status, limit, offset } = {}) =>
  apiFetchJson(`/api/orders${qs({ businessId, shopId, status, limit, offset })}`);
export const getOrder = async (orderId) => apiFetchJson(`/api/orders/${orderId}`);
export const updateOrderStatus = async (orderId, status) =>
  apiFetchJson(`/api/orders/${orderId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
export const convertOrderToSale = async (orderId, payload) =>
  apiFetchJson(`/api/orders/${orderId}/convert-to-sale`, { method: 'POST', body: JSON.stringify(payload || {}) });

// --- Stock ---
export const getInventory = async ({ businessId, shopId, lowStockOnly } = {}) =>
  apiFetchJson(`/api/inventory${qs({ businessId, shopId, lowStockOnly })}`);
export const getInventoryMovements = async ({ businessId, productId, limit, offset } = {}) =>
  apiFetchJson(`/api/inventory/movements${qs({ businessId, productId, limit, offset })}`);
export const adjustInventory = async (payload) =>
  apiFetchJson('/api/inventory/adjust', { method: 'POST', body: JSON.stringify(payload) });

// --- Clients ---
export const getCustomers = async ({ businessId, limit, offset } = {}) =>
  apiFetchJson(`/api/customers${qs({ businessId, limit, offset })}`);
export const createCustomer = async (payload) => apiFetchJson('/api/customers', { method: 'POST', body: JSON.stringify(payload) });
export const updateCustomer = async (customerId, payload) =>
  apiFetchJson(`/api/customers/${customerId}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteCustomer = async (customerId) => apiFetchJson(`/api/customers/${customerId}`, { method: 'DELETE' });

// --- Ventes ---
export const createSale = async (payload) => apiFetchJson('/api/sales', { method: 'POST', body: JSON.stringify(payload) });
export const getSales = async ({ businessId, shopId, limit, offset } = {}) =>
  apiFetchJson(`/api/sales${qs({ businessId, shopId, limit, offset })}`);
export const getSale = async (saleId) => apiFetchJson(`/api/sales/${saleId}`);
export const cancelSale = async (saleId) => apiFetchJson(`/api/sales/${saleId}/cancel`, { method: 'POST' });

// --- Crédit client / dette / paiements (Mission 02) ---
export const addSalePayment = async (saleId, payload) =>
  apiFetchJson(`/api/sales/${saleId}/payments`, { method: 'POST', body: JSON.stringify(payload) });
export const getCustomerCredit = async (customerId) => apiFetchJson(`/api/customers/${customerId}/credit`);
export const getReceivables = async ({ businessId, shopId } = {}) =>
  apiFetchJson(`/api/receivables${qs({ businessId, shopId })}`);

// --- Factures ---
export const createInvoiceFromSale = async (saleId, payload) =>
  apiFetchJson(`/api/invoices/from-sale/${saleId}`, { method: 'POST', body: JSON.stringify(payload || {}) });
export const createManualInvoice = async (payload) =>
  apiFetchJson('/api/invoices', { method: 'POST', body: JSON.stringify(payload) });
export const getInvoices = async ({ businessId, limit, offset } = {}) =>
  apiFetchJson(`/api/invoices${qs({ businessId, limit, offset })}`);
export const getInvoice = async (invoiceId) => apiFetchJson(`/api/invoices/${invoiceId}`);

// --- Dashboard ---
export const getDashboardSummary = async ({ businessId, shopId, from, to } = {}) =>
  apiFetchJson(`/api/dashboard/summary${qs({ businessId, shopId, from, to })}`);
export const getShopDashboard = async ({ businessId, shopId, from, to } = {}) =>
  getDashboardSummary({ businessId, shopId, from, to });
export const getShopProducts = async ({ businessId, shopId, limit, offset } = {}) =>
  getProducts({ businessId, shopId, limit, offset });
